import { Text, VStack, HStack, AspectRatio } from '@chakra-ui/react'
import { useInView } from 'react-intersection-observer'
import { useTrail } from 'react-spring'
import Image from 'next/image'
import { useTranslation, Trans } from 'next-i18next'

import ContentLayout from '@/features/aboutUs/components/ContentLayout'
import { AnimatedText, DEFAULT_SPRING_CONFIG } from '@/lib/springComponent'

function AboutOurCompany(): React.ReactElement {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const springs = useTrail(3, {
    to: {
      transform: inView ? 'translate3d(0, 0px, 0)' : 'translate3d(0, 32px, 0)',
      opacity: inView ? 1 : 0,
    },
    config: DEFAULT_SPRING_CONFIG,
  })

  const { t } = useTranslation('aboutUs')

  return (
    <ContentLayout
      imagePlacement="right"
      bgImage={{
        base: '/images/resources/form/our-company-mobile-bg.jpg',
        lg: '/images/resources/form/our-company-desktop-bg.jpg',
      }}
      bgColor="white"
      imageAlt="Our company"
    >
      <VStack
        ref={ref}
        spacing={{ base: 4, md: 8 }}
        sx={{
          alignItems: 'stretch',
          fontSize: 'xl',
          color: 'gray.900',
        }}
      >
        <VStack
          ref={ref}
          spacing="2"
          sx={{
            alignItems: 'stretch',
          }}
        >
          <Text
            as="h1"
            textStyle="landingSectionTitle"
            sx={{
              textAlign: {
                base: 'center',
                lg: 'left',
              },
              mb: 6,
            }}
          >
            <Trans t={t} i18nKey="aboutOurCompany.title">
              <Text as="span" sx={{ fontWeight: 'normal' }}>
                About
              </Text>
              Data Wow
            </Trans>
          </Text>
          <Text
            as="h2"
            textStyle="landingSectionSubtitle"
            sx={{
              textAlign: 'inherit',
              fontWeight: 'medium',
              fontSize: {
                base: 'xl',
                md: '2xl',
              },
            }}
          >
            {t('aboutOurCompany.subtitle')}
          </Text>
        </VStack>
        <AnimatedText style={springs[0]}>
          <Trans t={t} i18nKey="aboutOurCompany.paragraph.0">
            <Text as="strong" sx={{ fontWeight: 'medium' }}>
              Data Wow
            </Text>{' '}
            ผู้ให้บริการพัฒนาโมเดล AI และให้คำปรึกษาด้าน Data Science
            และการจัดการข้อมูลและยัง
            <Text as="strong" sx={{ fontWeight: 'medium' }}>
              ได้รับการรับรองมาตรฐาน ISO27001 ที่เป็นมาตรฐาน
              การจัดการความมั่นคงปลอดภัยสำหรับสารสนเทศ (ISMS) และ ISO27701
              (PIMS) ที่เป็นมาตรฐานด้านการจัดการข้อมูลส่วนบุคคลระดับสากล
            </Text>{' '}
            จึงเชี่ยวชาญด้านการจัดการและวางระบบให้ข้อมูลมีความปลอดภัยและความ
            เป็นส่วนตัว
          </Trans>
        </AnimatedText>
        <AnimatedText style={springs[1]}>
          <Trans t={t} i18nKey="aboutOurCompany.paragraph.1">
            ปัจจุบันพวกเราดูแลความปลอดภัยและความเป็นส่วนตัวให้กับข้อมูลส่วนบุคคล
            เป็นจำนวนมากกว่าหลายสิบล้านข้อมูล
            โดยมีทีมนักกฎหมายที่เชี่ยวชาญเรื่อง PDPA
            ที่มีประสบกาณ์ให้คำปรึกษาและจัดทำนโยบายและสัญญาให้กับธุรกิจในหลากหลาย
            อุตสาหกรรม
          </Trans>
        </AnimatedText>
        <AnimatedText style={springs[2]}>
          <Trans t={t} i18nKey="aboutOurCompany.paragraph.2">
            ทั้งยังมีซอฟต์แวร์ที่ช่วยธุรกิจในการบริหารจัดการข้อมูลส่วนบุคคลให้สอดคล้อง
            กับ PDPA ที่
            <Text as="strong" sx={{ fontWeight: 'medium' }}>
              มีผู้ใช้งานมากกว่า 2,000 คน
            </Text>{' '}
            จากทั้งธุรกิจขนาดเล็กไปจนถึงบริษัท เอกชนขนาดใหญ่
          </Trans>
        </AnimatedText>
        <HStack
          spacing="4"
          sx={{
            pt: 8,
            alignItems: 'center',
            justifyContent: { base: 'flex-start', md: 'flex-start' },
          }}
        >
          <AspectRatio ratio={210 / 80} sx={{ w: '205px' }}>
            <Image
              src="/images/resources/form/iso-black.svg"
              alt="ISO 27001"
              quality="100"
              objectFit="contain"
              layout="fill"
            />
          </AspectRatio>
        </HStack>
      </VStack>
    </ContentLayout>
  )
}

export default AboutOurCompany
